import { isDefined } from "@clipboard-health/util-ts";

import { type GradientVariant } from "../types";

interface GetGradientVariantParams {
  scoreChange: number;
  currentScore: number;
  latenessMinutes?: number;
  newlyRestricted: boolean;
  isSecondShiftInDouble: boolean;
  wasBookedWithShortLeadTime: boolean;
  didLosePointsForTimeliness: boolean;
  penaltyStartMinutes?: number;
}

/**
 * Maps shift context to a GradientVariant for the EC clipboard score page background.
 * Logic mirrors ClipboardScoreBottomSheet.getBackgroundGradient() mapped to
 * the three gradient variants: "positive", "negative", "warning".
 */
export function getGradientVariant(params: GetGradientVariantParams): GradientVariant {
  const {
    scoreChange,
    currentScore,
    latenessMinutes,
    newlyRestricted,
    isSecondShiftInDouble,
    wasBookedWithShortLeadTime,
    didLosePointsForTimeliness,
    penaltyStartMinutes,
  } = params;

  // Negative when the worker's score is below zero
  if (currentScore < 0) {
    return "negative";
  }

  // Warning for grace period: late but below penalty threshold
  if (
    isDefined(penaltyStartMinutes) &&
    isDefined(latenessMinutes) &&
    latenessMinutes >= 5 &&
    latenessMinutes < penaltyStartMinutes
  ) {
    return "warning";
  }

  // Positive for earned points or special scenarios (double shifts, last minute bookings without penalty)
  if (
    scoreChange > 0 ||
    isSecondShiftInDouble ||
    (wasBookedWithShortLeadTime && !didLosePointsForTimeliness)
  ) {
    return "positive";
  }

  // Negative for newly restricted OR late past penalty threshold with negative score change and score < 100
  if (
    newlyRestricted ||
    (isDefined(penaltyStartMinutes) &&
      isDefined(latenessMinutes) &&
      latenessMinutes >= penaltyStartMinutes &&
      scoreChange < 0 &&
      currentScore < 100)
  ) {
    return "negative";
  }

  // Warning for remaining cases
  return "warning";
}
